"use client";
import { useState, useEffect } from "react";
import { BASEAPIURL, token } from "../env";
import GuestRoomSelector from "./GuestRoomSelector";
import "./roombooking.css";

const RoomBooking = () => {
  // Step Management
  const [step, setStep] = useState(1);
  // Member info
  const [memberId] = useState(sessionStorage.getItem("MemberID") || "");
  const [memberName] = useState(sessionStorage.getItem("MemberName") || "");
  const [memberMobile] = useState(sessionStorage.getItem("MemMobileNo") || "");
  // Step 1: Booking Criteria
  const [checkInDate, setCheckInDate] = useState("");
  const [checkOutDate, setCheckOutDate] = useState("");
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomTypeId, setRoomTypeId] = useState("");
  const [roomCount, setRoomCount] = useState(1);
  const [locationId, setLocationId] = useState("");
  const [location, setLocation] = useState([]);
  const [guestCount, setGuestCount] = useState(1);
  // Availability
  const [isChecking, setIsChecking] = useState(false);
  const [availabilityResult, setAvailabilityResult] = useState(null);
  // Step 2: Guest List
  const [guests, setGuests] = useState([]);
  const [dependents, setDependents] = useState([]);
  // Pricing
  const [roomPrice, setRoomPrice] = useState(0);
  // Facility Details
  const [facilityDetails, setFacilityDetails] = useState(null);
  // Status
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingStatus, setBookingStatus] = useState(null);
  const [bookingMessage, setBookingMessage] = useState("");


  // Toast auto-dismiss
  useEffect(() => {
    if (bookingStatus) {
      const timer = setTimeout(() => {
        setBookingStatus(null);
        setBookingMessage("");
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [bookingStatus]);

  // Inside your component
useEffect(() => {
  // Set a timeout to close the alert after 7 seconds (7000 ms)
  const timer = setTimeout(() => {
    setBookingStatus(null);
  }, 7000);

  // Cleanup the timer if the component unmounts or bookingStatus changes
  return () => clearTimeout(timer);
}, []); // Empty dependency array means this runs once on mount

  // Load room types
  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        const res = await fetch(`${BASEAPIURL}/GetRoomTypeList`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tokenData: token }),
        });
        const text = await res.text();
        let json;
        try {
          json = JSON.parse(text);
        } catch {
          setError("Failed to parse room types response.");
          return;
        }

        if (Array.isArray(json.data)) {
          const formattedRooms = json.data.map(room => ({
            ...room,
            FacilityID: room.Facility_ID,
          }));
          setRoomTypes(formattedRooms);

          const room = formattedRooms.find(r => String(r.RoomType_ID) === String(roomTypeId)) || formattedRooms[0];
          if (room) {
            setRoomTypeId(String(room.RoomType_ID));
            setRoomPrice(parseFloat(room.RoomPrice) || 0);
            if (room.FacilityID) {
              fetchFacilityDetails(room.FacilityID);
            }
          }
        } else {
          setError("No room types available.");
        }
      } catch (err) {
        setError("Failed to load room types.");
        console.error("RoomTypes fetch error:", err);
      }
    };
    fetchRoomTypes();
  }, [roomTypeId]);

  // Update price and facility on room type change
  useEffect(() => {
    if (roomTypeId && roomTypes.length > 0) {
      const selectedRoom = roomTypes.find(r => String(r.RoomType_ID) === String(roomTypeId));
      if (selectedRoom) {
        setRoomPrice(parseFloat(selectedRoom.RoomPrice) || 0);
        if (selectedRoom.FacilityID) {
          fetchFacilityDetails(selectedRoom.FacilityID);
        }
      }
    }
  }, [roomTypeId, roomTypes]);

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await fetch(`${BASEAPIURL}/LocationList`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tokenData: token }),
        });
        const json = await res.json();
        if (Array.isArray(json.data)) {
          setLocation(json.data);
          if (!locationId) setLocationId(String(json.data[0]?.LocID || ""));
        } else {
          setError("No locations available.");
        }
      } catch (err) {
        setError("Failed to load locations.");
      }
    };
    fetchLocations();
  }, [locationId]);

  // Fetch facility details by ID
  const fetchFacilityDetails = async (facilityId) => {
    try {
      const url = `${BASEAPIURL}/GetFacilityByID?FacilityID=${facilityId}&Token=${encodeURIComponent(token)}`;
      const res = await fetch(url, { method: "GET" });
      const text = await res.text();
      console.log("Facility API Response:", text); // Debug log
      let json;
      try {
        json = JSON.parse(text);
      } catch (e) {
        console.error("Parse Error:", e, text);
        setError("Failed to parse facility details response.");
        setFacilityDetails(null);
        return;
      }

      if (json.status === "true" && json.data) {
        console.log("Facility Details:", json.data); // Debug log
        setFacilityDetails({
          ...json.data,
          Facility_Image: Array.isArray(json.data.Facility_Image) ? json.data.Facility_Image : [],
        });
      } else {
        console.warn("Facility not found:", json.Message);
        setError(json.Message || "Facility details not found.");
        setFacilityDetails(null);
      }
    } catch (err) {
      console.error("Facility fetch error:", err);
      setError("Failed to fetch facility details.");
      setFacilityDetails(null);
    }
  };

  // Handle availability check
  const handleAvailabilityCheck = async () => {
    setError(null);
    setIsChecking(true);

    if (!checkInDate || !checkOutDate || !roomTypeId || !locationId || roomCount < 1) {
      setError("Please fill all required fields.");
      setIsChecking(false);
      return;
    }
    if (new Date(checkOutDate) <= new Date(checkInDate)) {
      setError("Check-out date must be after check-in date.");
      setIsChecking(false);
      return;
    }

    try {
      const res = await fetch(
        `   ${BASEAPIURL}/GetRoomAvailable?Checkin=${checkInDate}&Checkout=${checkOutDate}&Rtype_id=${roomTypeId}&RoomCount=${roomCount}&LocID=${locationId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tokenData: token }),
        }
      );
      const text = await res.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch {
        setError("Failed to parse availability response.");
        setIsChecking(false);
        return;
      }
      if (json.data) {
        setAvailabilityResult(json.data);
        const selectedRoom = roomTypes.find(r => String(r.RoomType_ID) === roomTypeId);
        if (selectedRoom?.FacilityID) {
          fetchFacilityDetails(selectedRoom.FacilityID);
        }
      } else {
        setError(json.Message || "No rooms available.");
      }
    } catch (err) {
      setError("Failed to check availability. Please try again.");
      console.error("Availability Check Error:", err);
    } finally {
      setIsChecking(false);
    }
  };

  // Initialize guests when moving to Step 2
  useEffect(() => {
    if (step === 2 && roomCount > 0 && guests.length === 0) {
      setGuests([{
        id: 1,
        bookingFor: "Self",
        name: memberName,
        contact: memberMobile,
        dependentId: "",
      }]);
      setGuestCount(1);
    }
  }, [step, roomCount, memberName, memberMobile, guests]);

  // Fetch dependents in Step 2
  useEffect(() => {
    if (step === 2 && memberId) {
      const fetchDeps = async () => {
        try {
          const res = await fetch(`   ${BASEAPIURL}/DependentListByMemID?MemID=${memberId}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ tokenData: token }),
          });
          const json = await res.json();
          if (Array.isArray(json.data)) {
            setDependents(json.data);
          } else {
            setError("No dependents found.");
          }
        } catch (err) {
          setError("Failed to load dependents.");
          console.error("Fetch dependents failed:", err);
        }
      };
      fetchDeps();
    }
  }, [step, memberId]);

  // Handle guest change
  const handleGuestChange = (id, field, value) => {
    setGuests(prev => prev.map(g => {
      if (g.id !== id) return g;
      let updates = { [field]: value };
      if (field === "bookingFor") {
        if (value === "Self") {
          updates.name = memberName;
          updates.contact = memberMobile;
          updates.dependentId = "";
        } else if (value === "Guest") {
          updates.name = "";
          updates.contact = "";
          updates.dependentId = "";
        } else if (value === "Dependent") {
          updates.name = "";
          updates.contact = "";
          updates.dependentId = "";
        }
      }
      return { ...g, ...updates };
    }));
  };

  // Handle dependent selection with contact validation
  const handleDependentSelect = (id, depId) => {
    const dep = dependents.find(d => String(d.DeptID) === depId);
    if (dep) {
      const contact = dep.Contact || dep.MobileNo || "";
      if (contact && !/^[6-9]\d{9}$/.test(contact)) {
        setError(`Invalid contact number for dependent ${dep.Name}. Please update in profile or enter manually.`);
      }
      setGuests(prev => prev.map(g =>
        g.id === id ? {
          ...g,
          dependentId: depId,
          name: dep.Name,
          contact: contact || "",
        } : g
      ));
    }
  };

  // Add guest and sync guestCount
  const addGuest = () => {
    const maxGuests = 1;
    if (guests.length >= maxGuests) {
      setError("Only one guest is allowed per booking.");
      return;
    }
    const newId = Math.max(...guests.map(g => g.id), 0) + 1;
    setGuests([...guests, {
      id: newId,
      bookingFor: "Guest",
      name: "",
      contact: "",
      dependentId: "",
    }]);
    setGuestCount(guests.length + 1);
  };

  // Remove guest and sync guestCount
  const removeGuest = (id) => {
    if (guests.length <= 1) return;
    setGuests(guests.filter(g => g.id !== id));
    setGuestCount(guests.length - 1);
  };

  // Calculate nights and totals
  const nights = checkInDate && checkOutDate
    ? Math.max(1, Math.ceil((new Date(checkOutDate) - new Date(checkInDate)) / (1000 * 60 * 60 * 24)))
    : 1;
  const totalPrice = roomCount * nights * roomPrice;

  // Validate inputs
  const validateGuests = () => {
    const guest = guests[0];
    if (!guest) return "At least one guest is required.";
    if (!guest.name.trim()) return "Name is required for the guest.";
    if (guest.bookingFor === "Guest" || guest.bookingFor === "Dependent") {
      if (!/^[6-9]\d{9}$/.test(guest.contact)) {
        return "Enter a valid 10-digit Indian mobile number for the guest.";
      }
    }
    return null;
  };

  // Final submit
  const handleSubmitBooking = async () => {
    const validationError = validateGuests();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      const checkInTime = facilityDetails?.CheckInTime || "00:00:00";
      const checkOutTime = facilityDetails?.CheckOutTime || "00:00:00";
      const selectedRoom = roomTypes.find(r => String(r.RoomType_ID) === roomTypeId);
      const selectedLocation = location.find(l => String(l.LocID) === locationId);
      const guest = guests[0];
      const bookingDate = new Date().toISOString().slice(0, 19);
      const payload = {
        BookingID: 0,
        Member_ID: parseInt(memberId),
        RoomType_ID: parseInt(roomTypeId),
        RoomType: selectedRoom?.RoomType || "",
        Member_Name: guest.name,
        Member_Contact: guest.contact,
        TotalRoom: roomCount,
        CheckInDate: `${checkInDate}T${checkInTime}`,
        CheckOutDate: `${checkOutDate}T${checkOutTime}`,
        BookingDate: bookingDate,
        BookingFor: guest.bookingFor,
        ExtraBed: 0,
        Location_ID: locationId,
        Location: selectedLocation?.LocName || "",
      };
      console.log("Booking Payload:", JSON.stringify({ tokenData: token, RBooking: payload })); // Debug log
      const res = await fetch(`${BASEAPIURL}/InsertRoomBooking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tokenData: token, RBooking: payload }),
      });
      const resultText = await res.text();
      console.log("Booking API Response:", resultText); // Debug log
      let result;
      try {
        result = JSON.parse(resultText);
      } catch {
        setError("Failed to parse booking response.");
        setIsSubmitting(false);
        return;
      }
      if (result.status === "true") {
        setBookingStatus("success");
        setBookingMessage(result.Message || "Your booking has been confirmed!");
        setStep(1);
        setGuests([]);
        setAvailabilityResult(null);
        setCheckInDate("");
        setCheckOutDate("");
        setRoomCount(1);
        setGuestCount(1);
        setRoomTypeId(roomTypes[0]?.RoomType_ID || "");
        setLocationId(location[0]?.LocID || "");
        setFacilityDetails(null);
      } else {
        setError(result.Message || "Booking failed. Please try again.");
      }
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper for date formatting
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="container-fluid py-4">
      <h3 className="text-center mb-4 fw-bold text-primary">🏨 Room Booking</h3>

      {/* Toast Alerts */}
      {bookingStatus === "success" && (
        <div className="alert alert-success alert-dismissible fade show text-center" role="alert">
          ✅ {bookingMessage}
          <button type="button" className="btn-close" onClick={() => setBookingStatus(null)} aria-label="Close alert"></button>
        </div>
      )}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          ❌ {error}
          <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close alert"></button>
        </div>
      )}

      {/* Step 1: Availability Check */}
      {step === 1 && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{
            backgroundImage: `url(${facilityDetails?.BannerImage || "/images/room-booking-banner.jpg"})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div
            className="bg-white rounded-3 shadow px-4 py-3 w-100"
            style={{ maxWidth: "1100px", marginTop: "2rem" }}
          >
            <h5 className="text-center mb-4">🛏️ Book Hotels and Homestays</h5>
            <form className="d-flex flex-column flex-md-row gap-3 align-items-end justify-content-between" id="book">
              <div className="flex-grow-1">
                <label htmlFor="roomType" className="form-label">
                  <i className="bi bi-building"></i> Room Type
                </label>
                <select
                  id="roomType"
                  className="form-select rounded-pill"
                  value={roomTypeId}
                  onChange={(e) => setRoomTypeId(e.target.value)}
                  disabled={!roomTypes.length}
                >
                  {roomTypes.length ? (
                    roomTypes.map(room => (
                      <option key={room.RoomType_ID} value={room.RoomType_ID}>
                        {room.RoomType} - ₹{parseFloat(room.RoomPrice || 0).toFixed(2)}
                      </option>
                    ))
                  ) : (
                    <option value="">No room types available</option>
                  )}
                </select>
              </div>
              <div>
                <label htmlFor="checkIn" className="form-label">
                  <i className="bi bi-calendar-check"></i> Check-in
                </label>
                <input
                  id="checkIn"
                  type="date"
                  className="form-control rounded-pill"
                  value={checkInDate}
                  onChange={(e) => setCheckInDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
              <div>
                <label htmlFor="checkOut" className="form-label">
                  <i className="bi bi-calendar-x"></i> Check-out
                </label>
                <input
                  id="checkOut"
                  type="date"
                  className="form-control rounded-pill"
                  value={checkOutDate}
                  onChange={(e) => setCheckOutDate(e.target.value)}
                  min={checkInDate || new Date().toISOString().split("T")[0]}
                />
              </div>
              <div className="flex-grow-1">
                <GuestRoomSelector
                  rooms={roomCount}
                  setRooms={setRoomCount}
                  adults={guestCount}
                  setAdults={setGuestCount}
                  customClass="rounded-pill form-select"
                />
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-warning px-4 py-2 fw-bold rounded-pill"
                  onClick={handleAvailabilityCheck}
                  disabled={isChecking}
                >
                  {isChecking ? (
                    <span><i className="bi bi-hourglass-split"></i> Checking...</span>
                  ) : (
                    <span><i className="bi bi-search"></i> SEARCH</span>
                  )}
                </button>
              </div>
            </form>




            {/* Room Detail Card */}
            {availabilityResult && (
              <div className="mt-4">
                <div className="alert alert-success alert-dismissible fade show text-center" role="alert">
                  ✅ Booking is available
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setBookingStatus(null)}
                    aria-label="Close alert"
                  ></button>
                </div>
                {roomTypes.map(room => {
                  if (String(room.RoomType_ID) !== roomTypeId) return null;
                  const images = Array.isArray(facilityDetails?.Facility_Image) && facilityDetails.Facility_Image.length > 0
                    ? facilityDetails.Facility_Image
                    : ["/images/placeholder-room.jpg"];

                  return (
                    <div
                      key={room.RoomType_ID}
                      className="card border-0 shadow-sm overflow-hidden"
                      style={{
                        borderRadius: "12px",
                        backgroundColor: "#f8f9fc",
                      }}
                    >
                      <div className="row g-0">
                        <div className="col-md-4">
                          <div id={`roomImageCarousel-${room.RoomType_ID}`} className="carousel slide" data-bs-ride="carousel">
                            <div className="carousel-inner">
                              {images.map((image, index) => (
                                <div key={index} className={`carousel-item ${index === 0 ? 'active' : ''}`}>
                                  <img
                                    src={image}
                                    alt={`${facilityDetails?.Facility_Name || room.RoomType} Image ${index + 1}`}
                                    className="d-block w-100"
                                    style={{
                                      objectFit: "cover",
                                      borderTopLeftRadius: "12px",
                                      borderBottomLeftRadius: "12px",
                                      height: "250px", // Fixed height for consistency
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                            {images.length > 1 && (
                              <>
                                <button
                                  className="carousel-control-prev"
                                  type="button"
                                  data-bs-target={`#roomImageCarousel-${room.RoomType_ID}`}
                                  data-bs-slide="prev"
                                  aria-label="Previous Image"
                                >
                                  <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                </button>
                                <button
                                  className="carousel-control-next"
                                  type="button"
                                  data-bs-target={`#roomImageCarousel-${room.RoomType_ID}`}
                                  data-bs-slide="next"
                                  aria-label="Next Image"
                                >
                                  <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="col-md-8">
                          <div className="card-body d-flex flex-column justify-content-center h-100 p-4">
                            <h5 className="card-title mb-1 fw-bold text-dark">
                              {room.RoomType}
                            </h5>
                            {facilityDetails?.Facility_Name && (
                              <p className="text-primary mb-1">
                                <strong>{facilityDetails.Facility_Name}</strong>
                              </p>
                            )}
                            {facilityDetails?.Facility_Description && (
                              <p className="card-text text-muted small mb-2">
                                {facilityDetails.Facility_Description.replace(/["]+/g, "")}
                              </p>
                            )}
                            <p className="text-muted mb-2">
                              <small>
                                {nights} night(s) × {roomCount} room(s) • {guests.length} guest(s)
                              </small>
                            </p>
                            {availabilityResult.availableRooms && (
                              <p className="text-success small mb-2">
                                {availabilityResult.availableRooms} rooms available
                              </p>
                            )}
                            <div className="d-flex justify-content-between align-items-center mt-2">
                              <div>
                                <p className="mb-0"><strong>₹{roomPrice.toFixed(2)}/night</strong></p>
                                <p className="text-success fw-bold mb-0">Total: ₹{totalPrice.toFixed(2)}</p>
                              </div>
                              <button
                                className="btn btn-primary px-4 py-2 rounded-pill"
                                onClick={() => setStep(2)}
                              >
                                Proceed →
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Guest Details */}
      {step === 2 && (
        <div className="row g-5">
          <div className="col-lg-7">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="text-dark mb-0">
                <i className="bi bi-people me-2 text-primary"></i>
                Guest Information
              </h5>
            </div>
            {guests.length === 0 ? (
              <p className="text-muted">No guest added.</p>
            ) : (
              <ul className="list-group list-group-flush">
                {guests.map((guest) => (
                  <li
                    key={guest.id}
                    className="list-group-item border rounded-3 shadow-sm mb-3 p-3"
                    style={{ backgroundColor: '#fdfdfd' }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <small className="badge bg-secondary">Guest #{guest.id}</small>
                    </div>
                    <div className="mb-3">
                      <label htmlFor={`bookingFor-${guest.id}`} className="form-label">
                        <i className="bi bi-person-badge text-info"></i>{" "}
                        <strong>Booking For</strong>
                      </label>
                      <select
                        id={`bookingFor-${guest.id}`}
                        className="form-select rounded-pill"
                        value={guest.bookingFor}
                        onChange={(e) => handleGuestChange(guest.id, "bookingFor", e.target.value)}
                      >
                        <option value="Self">👤 Self ({memberName})</option>
                        <option value="Dependent">👨‍👩‍👧 Dependent</option>
                        <option value="Guest">🧑 Guest</option>
                      </select>
                    </div>
                    {guest.bookingFor === "Dependent" && (
                      <div className="mb-3">
                        <label htmlFor={`dependent-${guest.id}`} className="form-label">
                          <i className="bi bi-people-fill text-success"></i> Select Dependent
                        </label>
                        <select
                          id={`dependent-${guest.id}`}
                          className="form-select rounded-pill"
                          value={guest.dependentId}
                          onChange={(e) => handleDependentSelect(guest.id, e.target.value)}
                        >
                          <option value="">-- Choose Dependent --</option>
                          {dependents.map((dep) => (
                            <option key={dep.DeptID} value={dep.DeptID}>
                              {dep.Name} ({dep.Relation}) • {dep.Contact || dep.MobileNo || "No contact"}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {(guest.bookingFor === "Guest" || guest.bookingFor === "Dependent") && (
                      <div className="row g-2">
                        <div className="col-md-6">
                          <label htmlFor={`name-${guest.id}`} className="form-label">
                            <i className="bi bi-person"></i> Full Name
                          </label>
                          <input
                            id={`name-${guest.id}`}
                            type="text"
                            className={`form-control rounded-pill ${guest.name.trim() ? "is-valid" : ""}`}
                            value={guest.name}
                            onChange={(e) => handleGuestChange(guest.id, "name", e.target.value)}
                            placeholder="Enter full name"
                          />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor={`contact-${guest.id}`} className="form-label">
                            <i className="bi bi-telephone"></i> Contact Number
                          </label>
                          <input
                            id={`contact-${guest.id}`}
                            type="tel"
                            className={`form-control rounded-pill ${/^[6-9]\d{9}$/.test(guest.contact) ? "is-valid" : guest.contact ? "is-invalid" : ""
                              }`}
                            value={guest.contact}
                            onChange={(e) => handleGuestChange(guest.id, "contact", e.target.value)}
                            placeholder="e.g. 9876543210"
                          />
                          {guest.contact && !/^[6-9]\d{9}$/.test(guest.contact) && (
                            <div className="invalid-feedback d-block">
                              Must be a valid 10-digit Indian number
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {guest.bookingFor === "Self" && (
                      <div className="alert alert-light p-2 mt-2 small">
                        <i className="bi bi-check-circle-fill text-success"></i>{" "}
                        Using your profile: <strong>{memberName}</strong>, {memberMobile}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <div className="d-flex gap-2 mt-4">
              <button
                className="btn btn-outline-secondary flex-grow-1 rounded-pill"
                onClick={() => setStep(1)}
              >
                ← Back
              </button>
              <button
                className="btn btn-success flex-grow-1 rounded-pill"
                onClick={handleSubmitBooking}
                disabled={isSubmitting || guests.length === 0}
              >
                {isSubmitting ? (
                  <span><i className="bi bi-hourglass-split"></i> Processing...</span>
                ) : (
                  <span><i className="bi bi-check-circle"></i> Confirm Booking</span>
                )}
              </button>
            </div>
          </div>
          <div className="col-lg-5">
            <div className="card bg-light p-4 sticky-top" style={{ top: '20px' }}>
              <h5 className="mb-3"><i className="bi bi-receipt"></i> Booking Summary</h5>
              <hr />
              <ul className="list-unstyled">
                <li className="mb-2"><i className="bi bi-calendar-check text-success"></i> <strong>Check-in:</strong> {formatDate(checkInDate)}</li>
                <li className="mb-2"><i className="bi bi-calendar-x text-danger"></i> <strong>Check-out:</strong> {formatDate(checkOutDate)}</li>
                <li className="mb-2"><i className="bi bi-door-open text-info"></i> <strong>Room Type:</strong> {roomTypes.find(r => String(r.RoomType_ID) === roomTypeId)?.RoomType || "N/A"}</li>
                <li className="mb-2"><strong>Rooms:</strong> {roomCount} / <strong>Nights:</strong> {nights}</li>
                <li className="mb-2"><strong>Guests:</strong> {guests.length}</li>
              </ul>
              <div className="p-3 bg-white rounded shadow-sm">
                <h6><i className="bi bi-currency-rupee"></i> Price Breakdown</h6>
                <div className="row">
                  <div className="col-6">Rate per Night</div>
                  <div className="col-6 text-end">₹{roomPrice.toFixed(2)} (includes all taxes)</div>
                </div>
                <div className="row">
                  <div className="col-6">Nights × Rooms</div>
                  <div className="col-6 text-end">{nights} × {roomCount}</div>
                </div>
                <hr />
                <div className="row fw-bold">
                  <div className="col-6">Total</div>
                  <div className="col-6 text-end">₹{totalPrice.toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomBooking;
